// application/src/pages/GuestSmartAssistant/components/GuestSmartAssistantAccessibilityGuard.tsx

import React, { useState } from 'react';

import { useQuery } from '@tanstack/react-query';
import axios, { HttpStatusCode } from 'axios';
import { DataNotFoundAlert } from 'components/DataNotFoundAlert';
import { ErrorAlert } from 'components/ErrorAlert';
import { FullScreenLoader } from 'components/FullScreenLoader';
import { GuestSmartAssistantPasswordGuardModal } from 'pages/GuestSmartAssistant/components/GuestSmartAssistantPasswordGuardModal';
import { SmartAssistantForbiddenAlert } from 'pages/GuestSmartAssistant/components/SmartAssistantForbiddenAlert';
import { GuestSmartAssistantService } from 'services/guest-smart-assistant-service';
import { GUEST_SMART_ASSISTANT_TYPE_QUERY_KEY } from 'shared/constants/query-keys';

interface GuestSmartAssistantAccessibilityGuardProps {
  smartAssistantId?: string;
  type?: 'dashboard' | 'base';
  children: React.ReactNode;
}

export function GuestSmartAssistantAccessibilityGuard({
  smartAssistantId = '',
  type = 'base',
  children
}: GuestSmartAssistantAccessibilityGuardProps) {
  const [isPasswordModalOpen, setIsPasswordModalOpen] = useState(false);

  const smartAssistantAccessibilityCheckQuery = useQuery({
    queryKey: [GUEST_SMART_ASSISTANT_TYPE_QUERY_KEY, smartAssistantId],
    queryFn: () => GuestSmartAssistantService.checkAccessibility(smartAssistantId || '', type),
    retry: false,
    enabled: !!smartAssistantId
  });

  const handleQueryError = (error: unknown) => {
    const isAxiosError = axios.isAxiosError(error);
    const defaultErrorComponent = <ErrorAlert w="100svw" h="100svh" />;

    if (!isAxiosError) return defaultErrorComponent;

    if (error.response?.status === HttpStatusCode.NotFound) {
      return <DataNotFoundAlert />;
    } else if (error.response?.status === HttpStatusCode.Unauthorized) {
      if (!isPasswordModalOpen) {
        // Use setTimeout to avoid state updates during render
        setTimeout(() => setIsPasswordModalOpen(true), 0);
      }
    } else if (error.response?.status === HttpStatusCode.Forbidden) {
      return <SmartAssistantForbiddenAlert />;
    } else {
      return defaultErrorComponent;
    }
  };

  if (smartAssistantAccessibilityCheckQuery.isLoading) return <FullScreenLoader />;
  if (smartAssistantAccessibilityCheckQuery.isError) {
    const errorComponent = handleQueryError(smartAssistantAccessibilityCheckQuery.error);
    if (errorComponent) return errorComponent;
  }
  if ((!smartAssistantAccessibilityCheckQuery.data || !smartAssistantId) && !isPasswordModalOpen)
    return <ErrorAlert w="100svw" h="100svh" />;

  return (
    <>
      <GuestSmartAssistantPasswordGuardModal
        opened={isPasswordModalOpen}
        smartAssistantId={smartAssistantId}
        onAuthentificationSuccess={() => {
          setIsPasswordModalOpen(false);
          smartAssistantAccessibilityCheckQuery.refetch();
        }}
        onClose={() => GuestSmartAssistantService.isAuthenticated(smartAssistantId) && setIsPasswordModalOpen(false)}
      />
      {smartAssistantAccessibilityCheckQuery.data && <>{children}</>}
    </>
  );
}
